// components/gantt/RenderWorkPackage.js
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { YStack, XStack, H2, Paragraph, Button, Input } from 'tamagui';
import { Pressable } from 'react-native';
import { MaterialIcons, Feather } from '@expo/vector-icons';
import ErrorBoundary from '@utils/ErrorBoundary';
import apis from '@api/api'; // Assuming this is your configured API layer
import { OPEN_CONTEXT } from '@reduxLocal/actions/types'; // Assuming this is your Redux action type
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

// Import custom components used by this screen
import TaskGroup from './TaskGroup';
import { Card } from '@constants/Themed'; // Assuming a pre-styled Card component

interface RenderWorkPackageProps {
  item: any;
  projectId: string;
  refresh: () => void;
  setRetrievedWorkPackages: React.Dispatch<React.SetStateAction<any[]>>;
  drag: any;
  isActive: boolean;
  highlightedItem: any;
  setHighlightedItem: (item: any) => void;
  taskRef: any;
  projectGantt: any;
  onDeleteWorkPackage?: (item: any, deleteCallback: () => Promise<void>) => void;
}



/**
 * A fallback component displayed when there are no work packages.
 */
const EmptyState = () => {
  const dispatch = useDispatch();
  return (
    <YStack
      flex={1}
      justifyContent="center"
      alignItems="center"
      padding="$4"
      borderRadius="$6"
      backgroundColor="$background"
      elevation="$2"
      gap="$4"
      margin="$4"
    >
      <H2 textAlign="center">No Work Packages Found</H2>
      <Paragraph textAlign="center" theme="alt1">
        You can easily add one by clicking the{' '}
        <Feather name="folder-plus" size={20} style={{ marginHorizontal: 10 }} /> icon in the
        context menu.
            </Paragraph>
            <Button
        iconAfter={<Feather name="folder-plus" size={20} />}
        onPress={() => dispatch({ type: OPEN_CONTEXT })}
            >
        Open Context Menu
            </Button>
    </YStack>
  );
};

/**
 * Renders a single Work Package, including its title, action buttons, and a list of tasks.
 */
const RenderWorkPackage: React.FC<RenderWorkPackageProps> = ({
  item,
  projectId,
  refresh,
  setRetrievedWorkPackages,
  drag,
  isActive,
  highlightedItem,
  setHighlightedItem,
  taskRef,
  projectGantt,
  onDeleteWorkPackage, // New prop for delete callback
}) => {
  if (!item) {
    return <EmptyState />;
  }

  const [workPackageTitle, setWorkPackageTitle] = useState(item.work_package_title);

  // --- API Handlers ---

  const handleDeleteWorkPackage = async () => {
    if (onDeleteWorkPackage) {
      // Call parent's delete handler with work package data
      onDeleteWorkPackage(item, async () => {
        await apis.deleteWorkPackage({ workPackageId: item._id, projectId });
        refresh(); // Refresh the list after deletion
      });
    } else {
      // Fallback to direct deletion if no callback provided
      await apis.deleteWorkPackage({ workPackageId: item._id, projectId });
      refresh();
    }
  };

  const handleChangeWorkPackageTitle = async () => {
    if (workPackageTitle === item.work_package_title) return;
    await apis.changeWorkPackageTitle(item._id, projectId, workPackageTitle);
    // Optimistically update the local state
    setRetrievedWorkPackages((prev) =>
      prev.map((wp) =>
        wp._id === item._id ? { ...wp, work_package_title: workPackageTitle } : wp
      )
    );
  };

  // --- Callbacks ---

  const handleModifyTaskList = (updatedTaskList, workPackageId) => {
    setRetrievedWorkPackages((prev) =>
      prev.map((wp) =>
        wp._id === workPackageId ? { ...wp, task_list: updatedTaskList } : wp
      )
    );
  };

  const isHighlighted = highlightedItem?._id === item._id;
  const theme = themeSelector();

  return (
    <YStack
      gap="$3"
      padding="$3"
      borderRadius="$6"
      animation="bouncy"
      // Apply modern card styling with consistent background and borders
      shadowOffset={{ width: 0, height: isHighlighted ? 8 : 4 }}
      shadowOpacity={0.15}
      shadowRadius={isHighlighted ? 16 : 8}
      elevation={isHighlighted ? 8 : 4}
      pressStyle={{ scale: 0.98 }}
      onPress={() => setHighlightedItem?.(item)}
    >
        {/* Header Section */}
      <Pressable
        onLongPress={drag}
        disabled={isActive}
        style={({ pressed }) => ({
          opacity: pressed ? 0.8 : 1,
          transform: [{ scale: pressed ? 0.98 : 1 }]
        })}
      >
          <XStack
            justifyContent="space-between"
            alignItems="center"
            gap="$3"
            padding="$3"
            borderRadius="$4"
            backgroundColor="$background"
          >
            <Input
              flex={1}
              value={workPackageTitle}
              onChangeText={setWorkPackageTitle}
              onBlur={handleChangeWorkPackageTitle}
              placeholder="Work Package Title"
              size="$4"
            />
            <XStack gap="$2">
            {/* Drag Handle */}
            <Button
              icon={<Feather name="menu" size={20} color="white" />}
              circular
              onLongPress={drag}
              size="$3"
              theme="blue"
              style={{ opacity: isActive ? 0.5 : 1 }}
            />
              <Button
              icon={<Feather name="plus" size={20} color="white" />}
              circular
              onPress={() => {
                if (typeof taskRef.current?.openModal === 'function') {
                  taskRef.current.openModal(item._id);
                }
              }}
              size="$3"
              theme="green"
              />
              <Button
              icon={<Feather name="trash-2" size={20} color="white" />}
              circular
              onPress={handleDeleteWorkPackage}
              size="$3"
              theme="red"
              />
            </XStack>
          </XStack>
        </Pressable>

        {/* Task Group Section */}
        <YStack>
        <ErrorBoundary>
          <TaskGroup
            minify={projectGantt}
            modifyCallback={handleModifyTaskList}
            item={item}
            refresh={refresh}
            projectId={projectId}
            taskRef={taskRef}
            workPackageId={item._id}
          />
        </ErrorBoundary>
        </YStack>
      </YStack>
  );
};

export default RenderWorkPackage;
